import {
  DataError,
  useCatalogItem,
  useMeta,
  useSetCatalogVisibility,
  useUpdateCatalogItem,
} from '@numistic/data'
import { useTranslation } from '@numistic/i18n'
import { Alert, Card, CardContent, Field, Select } from '@numistic/ui'
import { createRoute, Link, useNavigate } from '@tanstack/react-router'
import { useMemo, useState } from 'react'
import { z } from 'zod'

import { PageLoading } from '../../components/page-loading.js'
import { ImagesManager } from '../../catalog/images-manager.js'
import { autoTitle, CATALOG_REF_KINDS, VisibilityBadge } from '../../catalog/shared.js'
import { VariantsEditor } from '../../catalog/variants-editor.js'
import { useErrorText } from '../../lib/errors.js'
import { requireSession } from '../../lib/guards.js'
import { MetaForm } from '../../meta/meta-form.js'
import { useRefResolver } from '../../meta/ref-resolver.js'
import { rootRoute } from '../root.js'

const Tab = z.enum(['data', 'images', 'variants'])

export const catalogEditRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/catalog/$id/edit',
  validateSearch: z.object({ tab: Tab.optional() }),
  beforeLoad: ({ context, location }) => requireSession(context, location),
  component: EditCatalogItemPage,
})

function EditCatalogItemPage() {
  const { t } = useTranslation()
  const errorText = useErrorText()
  const { id } = catalogEditRoute.useParams()
  const { tab = 'data' } = catalogEditRoute.useSearch()
  const navigate = useNavigate()
  const item = useCatalogItem(id)
  const meta = useMeta().data
  const update = useUpdateCatalogItem()
  const visibility = useSetCatalogVisibility()
  const resolve = useRefResolver(meta, CATALOG_REF_KINDS)
  // Версия, с которой начата правка: фоновое обновление карточки не пересоздаёт форму
  // (правки не теряются), а чужое изменение за это время даст конфликт версий, а не перезапись.
  const [baseVersion, setBaseVersion] = useState<number | null>(null)
  if (item.data && baseVersion === null) setBaseVersion(item.data.version)
  const prepare = useMemo(
    () => (meta && item.data ? autoTitle(meta, item.data.typeCode, resolve) : undefined),
    [meta, item.data, resolve],
  )

  if (item.error) return <Alert variant="destructive">{errorText.request(item.error)}</Alert>
  if (!item.data || !meta) return <PageLoading />
  const data = item.data
  if (!data.canEdit) return <Alert variant="destructive">{t('errors.FORBIDDEN')}</Alert>
  const typeName = meta.itemTypes.find((x) => x.code === data.typeCode)?.name.ru

  return (
    <div className="grid max-w-4xl gap-6">
      <div className="grid gap-1">
        <Link
          to="/catalog/$id"
          params={{ id }}
          className="text-sm text-muted-foreground hover:text-foreground"
        >
          ← {String(data.values.title ?? '')}
        </Link>
        <h1 className="flex items-center gap-3 text-2xl font-semibold">
          {t('catalog.edit')}: {typeName}
          <VisibilityBadge visibility={data.visibility} />
        </h1>
      </div>
      <nav className="flex gap-6 border-b" aria-label={t('catalog.edit')}>
        {Tab.options.map((x) => (
          <Link
            key={x}
            to="/catalog/$id/edit"
            params={{ id }}
            search={{ tab: x }}
            aria-current={tab === x ? 'page' : undefined}
            className="border-b-2 border-transparent px-1 pb-2 text-sm text-muted-foreground hover:text-foreground aria-[current=page]:border-primary aria-[current=page]:text-foreground"
          >
            {t(`catalog.tabs.${x}`)}
            {x === 'images' && data.images.length ? ` (${data.images.length})` : ''}
            {x === 'variants' && data.variants.length ? ` (${data.variants.length})` : ''}
          </Link>
        ))}
      </nav>
      <Card>
        <CardContent className="pt-6">
          {tab === 'data' ? (
            <MetaForm
              key={data.id}
              typeCode={data.typeCode}
              level="catalog"
              defaultValues={data.values}
              prepare={prepare}
              serverIssues={update.error instanceof DataError ? update.error.issues : undefined}
              header={
                data.ownerId === null ? (
                  <Field label={t('catalog.visibility')} className="max-w-xs">
                    <Select
                      value={data.visibility}
                      disabled={visibility.isPending}
                      onChange={(e) =>
                        visibility.mutate(
                          { id, visibility: e.target.value as 'public' | 'members' },
                          { onSuccess: (out) => setBaseVersion(out.version) },
                        )
                      }
                    >
                      <option value="public">{t('catalog.visibilities.public')}</option>
                      <option value="members">{t('catalog.visibilities.members')}</option>
                    </Select>
                  </Field>
                ) : null
              }
              footer={
                <>
                  {update.error instanceof DataError && update.error.code === 'VERSION_CONFLICT' ? (
                    <Alert variant="destructive">{t('catalog.conflict')}</Alert>
                  ) : update.error ? (
                    <Alert variant="destructive">{errorText.request(update.error)}</Alert>
                  ) : null}
                </>
              }
              pending={update.isPending}
              onSubmit={(values) =>
                update.mutate(
                  { id, version: baseVersion ?? data.version, values },
                  { onSuccess: () => void navigate({ to: '/catalog/$id', params: { id } }) },
                )
              }
            />
          ) : tab === 'images' ? (
            <ImagesManager subject={{ kind: 'catalog', id }} images={data.images} />
          ) : (
            <VariantsEditor itemId={id} typeCode={data.typeCode} variants={data.variants} />
          )}
        </CardContent>
      </Card>
    </div>
  )
}
